"use client"

import type { Product, Promotion } from "@/lib/types"
import { Heart, Eye, TrendingUp, Tag, Clock, ShoppingCart } from "lucide-react"
import Link from "next/link"
import { useState, useEffect } from "react"
import { toast } from "sonner"
import { useWishlist } from "@/lib/wishlist-context"
import { useCart } from "@/lib/cart-context"
import { getActivePromotionForProduct, calculateDiscountedPrice, formatDiscountBadge, getTimeRemaining } from "@/lib/promotion-utils"
import { formatNumber, formatPrice, buildFullImageUrl } from "@/lib/utils"

interface ProductCardProps {
  product: Product
  onUpdate?: () => void
}

export function ProductCard({ product, onUpdate }: ProductCardProps) {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist()
  const { addToCart } = useCart()
  const [promotion, setPromotion] = useState<Promotion | null>(null)
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [localLikes, setLocalLikes] = useState(product.likes || 0)

  const isLiked = product._id ? isInWishlist(product._id) : false

  useEffect(() => {
    setLocalLikes(product.likes || 0)
  }, [product.likes])

  useEffect(() => {
    // Charger la promotion active pour ce produit
    const loadPromotion = async () => {
      if (product._id) {
        const promo = await getActivePromotionForProduct(product._id)
        setPromotion(promo)
      }
    }
    loadPromotion()
  }, [product._id])

  // Mettre à jour le compte à rebours
  useEffect(() => {
    if (!promotion) return

    const updateTimer = () => {
      setTimeRemaining(getTimeRemaining(promotion.endDate))
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [promotion])

  const handleLike = async () => {
    if (!product._id) return

    try {
      if (isLiked) {
        removeFromWishlist(product._id)
        setLocalLikes(prev => Math.max(0, prev - 1))
        toast.success(`${product.name} retiré de votre panier`)
      } else {
        addToWishlist(product)
        setLocalLikes(prev => prev + 1)
        toast.success(`${product.name} ajouté à votre panier`)
      }

      // Notify parent to refresh if needed
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour des favoris:", error)
      toast.error("Erreur lors de la mise à jour des favoris")
    }
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Bloquer seulement si le stock est explicitement à 0 (rupture de stock réelle)
    // Permettre l'ajout si stock est null/undefined (stock non géré)
    if (!product._id || (product.stock === 0 && product.stock !== null && product.stock !== undefined)) return

    const finalPrice = promotion
      ? calculateDiscountedPrice(product.price, promotion)
      : product.price

    addToCart(
      product,
      1,
      finalPrice,
      promotion?._id || promotion?.id || null
    )

    toast.success(`${product.name} ajouté au panier`)
  }

  return (
    <div className="bg-white dark:bg-dark-background-secondary rounded-lg overflow-hidden border border-border dark:border-dark-border hover:shadow-lg transition-shadow">
      <Link href={`/produits/${product._id}`}>
        <div className="relative overflow-hidden bg-background-secondary dark:bg-dark-background h-48">
          <img
            src={buildFullImageUrl(product.images?.[0]) ?? "/placeholder.svg"}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-110 transition-transform"
          />

          {/* Badge promotion */}
          {promotion && (
            <div className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
              {formatDiscountBadge(promotion)}
            </div>
          )}

          <div className="absolute top-2 right-2 bg-primary text-white px-2 py-1 rounded text-xs font-medium">
            {product.category}
          </div>
        </div>
      </Link>

      <div className="p-4">
        <Link href={`/produits/${product._id}`}>
          <h3 className="font-semibold text-foreground dark:text-dark-foreground hover:text-primary transition line-clamp-2">
            {product.name}
          </h3>
        </Link>

        <p className="text-foreground-secondary dark:text-dark-foreground-secondary text-sm mt-1 line-clamp-2">
          {product.description}
        </p>

        {/* Prix avec promotion */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border dark:border-dark-border">
          {promotion ? (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-red-600">
                {formatPrice(calculateDiscountedPrice(product.price, promotion))}
              </span>
              <span className="text-sm text-gray-500 line-through">
                {formatPrice(product.price)}
              </span>
            </div>
          ) : (
            <span className="text-lg font-bold text-primary">{formatPrice(product.price)}</span>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0 && product.stock !== null && product.stock !== undefined}
              className={`p-2 rounded transition ${product.stock === 0 && product.stock !== null && product.stock !== undefined
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-primary/10 text-primary hover:bg-primary hover:text-white"
                }`}
              title={
                product.stock === null || product.stock === undefined
                  ? "Ajouter au panier"
                  : product.stock === 0
                    ? "Rupture de stock"
                    : "Ajouter au panier"
              }
            >
              <ShoppingCart size={18} />
            </button>
            <button
              onClick={handleLike}
              className={`p-2 rounded transition ${isLiked
                ? "bg-error/20 text-error"
                : "bg-background-secondary dark:bg-dark-background text-foreground-secondary hover:text-error"
                }`}
            >
              <Heart size={18} fill={isLiked ? "currentColor" : "none"} />
            </button>
          </div>
        </div>

        {/* Compte à rebours de la promotion */}
        {promotion && timeRemaining.days + timeRemaining.hours + timeRemaining.minutes + timeRemaining.seconds > 0 && (
          <div className="mt-2 flex items-center gap-1 text-xs text-orange-600 bg-orange-50 dark:bg-orange-950/30 px-2 py-1 rounded">
            <Clock size={12} />
            <span className="font-medium">
              {timeRemaining.days > 0 && `${timeRemaining.days}j `}
              {timeRemaining.hours}h {timeRemaining.minutes}m {timeRemaining.seconds}s
            </span>
          </div>
        )}

        {/* Compteur d'utilisation de la promotion */}
        {promotion?.maxUsageCount && (
          <div className="mt-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-600 dark:text-gray-400">
                {Math.max(promotion.maxUsageCount - promotion.currentUsageCount, 0) === 0
                  ? "Épuisée"
                  : `${Math.max(promotion.maxUsageCount - promotion.currentUsageCount, 0)} places`}
              </span>
              <span className="text-gray-500 font-medium">
                {promotion.currentUsageCount}/{promotion.maxUsageCount}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${promotion.currentUsageCount >= promotion.maxUsageCount
                  ? "bg-red-500"
                  : (promotion.currentUsageCount / promotion.maxUsageCount) >= 0.8
                    ? "bg-orange-500"
                    : "bg-green-500"
                  }`}
                style={{
                  width: `${Math.min((promotion.currentUsageCount / promotion.maxUsageCount) * 100, 100)}%`
                }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-secondary dark:text-dark-foreground-secondary">
          <span className="flex items-center gap-1">
            <Heart size={14} /> {formatNumber(localLikes)}
          </span>
          <span className="flex items-center gap-1">
            <Eye size={14} /> {formatNumber(product.views)}
          </span>
          <span className="flex items-center gap-1">
            <TrendingUp size={14} /> {formatNumber(product.sales)}
          </span>
        </div>
      </div>
    </div>
  )
}
